// This file is part of InvenioRDM
// Copyright (C) 2020-2024 CERN.
// Copyright (C) 2020-2021 Northwestern University.
// Copyright (C) 2021 Graz University of Technology.
//
// Invenio RDM Records is free software; you can redistribute it and/or modify it
// under the terms of the MIT License; see LICENSE file for more details.

// Overrides to add popup on Edit and New Version buttons
import { i18next } from "@translations/invenio_app_rdm/i18next";

import React, { Component } from "react";
import { Button, Grid, Icon, Message, Popup } from "semantic-ui-react";

import { EditButton } from "@js/invenio_app_rdm/landing_page/EditButton";
import { ShareButton } from "@js/invenio_app_rdm/landing_page/ShareOptions/ShareButton";
import { NewVersionButton } from "@js/invenio_rdm_records";
import PropTypes from "prop-types";
import { ManageButton } from "@js/invenio_app_rdm/landing_page/ManageButton";

export class RecordManagement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: "",
      popup: false,
      popup: {"edit": false, "new_version": false}
    };
  }

  render() {
    const {
      record,
      permissions,
      isDraft,
      isPreviewSubmissionRequest,
      currentUserId,
      recordOwnerUsername,
      groupsEnabled,
    } = this.props;

    const { error, popup } = this.state;
    const { id: recid } = record;
    const handleError = (errorMessage) => {
      this.setState({ error: errorMessage });
    };

    const handlePopup = (event, action) => {
      event.persist();
      if (event.type === "mouseenter") {
        if (action === "edit") {
          this.setState({ popup: {"edit": true, "new_version": false} });
        } else {
          this.setState({ popup: {"edit": false, "new_version": true} });
        }
      }
      if (event.type === "mouseleave") this.setState({ popup: {"edit": false, "new_version": false} });
    };

    const popupMessageEdit= "\
    By clicking the Edit button, a draft of this published record will be created.\
    Only references, keywords, or links to other Materials Cloud sections can be edited.\
    If you later want to create a new version of this record, you will first need to publish the draft or discard it.\
    "

    const popupMessageNewVersion= "\
    By clicking the New version button, a new version draft of this record will be created.\
    If you later want to edit the current version, you will first need to publish the new version draft or discard it.\
    "

    return (
      <Grid columns={1} className="record-management">
        {permissions.can_edit && permissions.can_new_version &&
        !isPreviewSubmissionRequest && (
          <Grid.Column>
            <Message info className="no-border-radius m-0">
              This record is published. You can either Edit this version or create a New version.
            </Message>
          </Grid.Column>
        )}

        {/* Admin button (disabled by permissions) */}
        {permissions.can_moderate && (
          <Grid.Column className="pb-5">
            <ManageButton recid={recid} recordOwnerUsername={recordOwnerUsername} />
          </Grid.Column>
        )}

        {/* Edit button on published record */}
        {permissions.can_edit && !isDraft && (
          <Grid.Column className={permissions.can_manage ? "pb-5 pt-5" : "pb-5"}>
            <Popup
              role="alert"
              open={popup["edit"]}
              content={popupMessageEdit}
              inverted={true}
              position={"top center"}
              size="small"
              trigger={
              <p
                onMouseEnter={(e) => handlePopup(e, "edit")} // Show popup on mouse over
                onMouseLeave={(e) => handlePopup(e, "edit")}   // Hide popup on mouse out
              >
                <EditButton recid={recid} onError={handleError}/>
              </p>
              }
            />
          </Grid.Column>
        )}

        {/* Edit button in requests */}
        {isPreviewSubmissionRequest && isDraft && (
          <Grid.Column className="pb-20">
            <Button
              fluid
              className="warning"
              size="medium"
              onClick={() => (window.location = `/uploads/${recid}`)}
              icon
              labelPosition="left"
            >
              <Icon name="edit" />
              {i18next.t("Edit")}
            </Button>
          </Grid.Column>
        )}

        {/* New version button on published record */}
        {permissions.can_new_version && !isPreviewSubmissionRequest && (
          <Grid.Column className="pt-5 pb-5">
            <Popup
            role="alert"
            open={popup["new_version"]}
            content={popupMessageNewVersion}
            inverted={true}
            position={"top center"}
            size="small"
            trigger={
            <p
              onMouseEnter={handlePopup} // Show popup on mouse over
              onMouseLeave={handlePopup}   // Hide popup on mouse out
            >
              <NewVersionButton
              fluid
              size="medium"
              record={record}
              onError={handleError}
              disabled={!permissions.can_new_version}
              />
            </p>
            }
          />
          </Grid.Column>
        )}

        {/* Share button on published record */}
        {permissions.can_manage && !isPreviewSubmissionRequest && (
          <Grid.Column className="pt-5">
            <ShareButton
              disabled={!permissions.can_update_draft}
              record={record}
              permissions={permissions}
              groupsEnabled={groupsEnabled}
            />
          </Grid.Column>
        )}

        {error && (
          <Grid.Row className="record-management">
            <Grid.Column>
              <Message negative>{error}</Message>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    );
  }
}

RecordManagement.propTypes = {
  record: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  isDraft: PropTypes.bool.isRequired,
  groupsEnabled: PropTypes.bool.isRequired,
  isPreviewSubmissionRequest: PropTypes.bool.isRequired,
  currentUserId: PropTypes.string.isRequired,
  recordOwnerUsername: PropTypes.object.isRequired,
};
